/**
 * SchultePage — «Таблицы Шульте» (раздел «Когнитивные навыки»).
 *
 * На экране ТАБЛИЦА 7×7 (49 чисел); пациент нажимает числа ПО ПОРЯДКУ от 1
 * до 49. Верное нажатие окрашивает ячейку зелёным (остаётся до конца),
 * нажатие мимо порядка — красной вспышкой и считается ошибкой (прогресс не
 * сбрасывается). Сверху — секундомер.
 *
 * Протокол методички (задание 9): «Включите секундомер. Найдите на таблице
 * цифры по порядку от 1 до 49. Запишите время…» → клик «Тренироваться» в
 * каталоге СРАЗУ открывает экран задания — отдельной страницы-инструкции
 * НЕТ. Секундомер (0:00) и таблица на месте, но таблица РАЗМЫТА и не
 * кликается, поверх звучит голосовая инструкция (mp3 из Storage); как
 * только озвучка закончилась — размытие снимается и секундомер СТАРТУЕТ
 * САМ (изучить таблицу заранее нельзя — она читаема только при идущем
 * секундомере). Страховки озвучки: файл не сыграл → инструкцию читает
 * синтез речи браузера; недоступен и он → поверх таблицы появляется
 * кнопка «Начать» (единственный случай, когда она есть). Задание
 * закрывается САМО по клику «49»
 * (автофиниш); страховки — кнопка «Готово» и мягкий лимит
 * (config.softLimitSec, по умолчанию 5 минут), время пишется в любом случае.
 *
 * Счёт — СЕРВЕР (api/app/scoring/schulte.py): страница присылает только
 * протокол нажатий [{n, atMs}] и момент досрочного финиша; время/ошибки/
 * контрольные точки сервер пересчитывает сам (клиентские числа не
 * принимаются — та же модель доверия, что счёт слов из transcript).
 *
 * Каждая таблица показывается 2 занятия подряд — как в «Беглости речи» и
 * «Зрительном восприятии»: занятие (день) N → таблица ceil(N/2)
 * (дни 1–2 → таблица 1 … дни 39–40 → таблица 20). Результаты — в ОБЩЕЙ
 * таблице trainer_reports: stepId 's01'..'s20' ПО ТАБЛИЦЕ (оба занятия пары
 * пишут в один шаг), algorithm_version='schulte-web-1'.
 *
 * Маршрут: /schulte — пациент сам; /schulte?patientId=UUID — запуск врачом
 * из карточки пациента. День программы = 1..40: первое прохождение — день 1,
 * дальше календарные дни от первой schulte-записи (фильтр stepId ^s\d+$);
 * ?day=N — проверить любой день. Содержимое таблиц — в db/schulte_bank.sql
 * (миграция 019); в таблицах 1/9/18 исправлены 3 опечатки печатной методички
 * (см. scripts/make_schulte_bank.py).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BrainCircuit,
  CheckCircle2,
  Loader2,
  Play,
  RotateCcw,
  Timer,
} from 'lucide-react';
// Клиент — общий cortivoxApi.js (тот же request-хелпер, что и у остальных
// тренажёров): getSchulteSession / saveSchulteReport добавлены туда же.
import {
  getAuthUserId,
  getSchulteSession,
  listTrainerReports,
  localToday,
  saveSchulteReport,
} from '../lib/cortivoxApi';

const PROGRAM_DAYS = 40;
const DEFAULT_SOFT_LIMIT_MS = 300_000; // мягкий лимит, если сервер не задал
const MAX_CLICKS = 300;                // страховка протокола (как на сервере)
const WRONG_FLASH_MS = 650;            // сколько горит красная вспышка промаха

// Голосовая инструкция: ОДИН mp3 на все 20 таблиц (Supabase Storage,
// bucket cortivox, папка schulte — как visual/ у «Зрительного восприятия»).
// Файла нет / браузер заглушил автоплей → фолбэк: синтез речи браузера,
// затем кнопка «Начать».
const INSTRUCTION_AUDIO_URL =
  'https://intellect.ezspeech.ai/storage/v1/object/public/cortivox/schulte/instruction.mp3';
const AUDIO_CAP_MS = 30_000;  // страховка от «зависшего» аудиофайла
// Текст для синтеза речи (фолбэк) — дословно как в mp3.
const INSTRUCTION_SPEECH =
  'Нажимайте числа по порядку: 1, 2, 3 и так далее до 49. '
  + 'Верное нажатие станет зелёным, а неверное красным. Начинайте.';

const SCHULTE_STEP_RE = /^s\d+$/; // шаги schulte-тренажёра в trainer_reports

// Занятие (день программы) 1..40 → таблица 1..20: каждую показывают
// 2 занятия подряд, stepId отчёта — ПО ТАБЛИЦЕ, как в «Зрительном восприятии».
const tableForDay = (d) => Math.ceil((Number(d) || 1) / 2);
const stepIdForDay = (d) => `s${String(tableForDay(d)).padStart(2, '0')}`;

const dayToUtc = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return Date.UTC(y || 1970, (m || 1) - 1, d || 1);
};

// День программы по истории schulte-записей: первое прохождение = день 1.
const resolveDayFromReports = (items, todayIso) => {
  const dates = (Array.isArray(items) ? items : [])
    .filter((it) => SCHULTE_STEP_RE.test(String(it?.stepId || '')))
    .map((it) => String(it?.scoredOn || '').slice(0, 10))
    .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
    .sort();
  if (dates.length === 0) return 1;
  const first = dates[0];
  if (todayIso <= first) return 1;
  const diffDays = Math.floor((dayToUtc(todayIso) - dayToUtc(first)) / 86_400_000);
  return Math.min(PROGRAM_DAYS, Math.max(1, diffDays + 1));
};

const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const plural = (n, one, few, many) => {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
};

const errorsLabel = (n) =>
  n === 0 ? 'Без ошибок' : `${n} ${plural(n, 'ошибка', 'ошибки', 'ошибок')}`;

const hintFromError = (err) => {
  const status = err?.status ?? err?.response?.status;
  if (status === 403 || status === 404) {
    return 'Если вы врач: открывайте тренажёр из карточки пациента — /schulte?patientId=UUID пациента.';
  }
  return null;
};

export default function SchultePage() {
  // phases: loading | ready | running | saving | done | error
  const [phase, setPhase] = useState('loading');
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [errorHint, setErrorHint] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [day, setDay] = useState(null);

  // Живое состояние задания
  const [elapsed, setElapsed] = useState(0);        // показ секундомера, мс
  const [found, setFound] = useState(() => new Set());   // верно найденные числа
  const [flashing, setFlashing] = useState(() => new Set()); // ячейки с красной вспышкой (индексы)
  const [errorCount, setErrorCount] = useState(0);

  const startedAtRef = useRef(0);     // Date.now() нажатия «Начать»
  const expectedRef = useRef(1);      // следующее число по порядку
  const clicksRef = useRef([]);       // протокол [{n, atMs}]
  const errorsRef = useRef(0);
  const finishedRef = useRef(false);
  const timerRef = useRef(0);         // interval секундомера
  const flashTimerRef = useRef(0);
  const softLimitRef = useRef(0);
  const audioRef = useRef(null);   // Audio голосовой инструкции
  const audioCapRef = useRef(0);   // страховка-таймер аудио
  const ttsGuardRef = useRef(0);   // страховка-таймер синтеза речи
  const [needManualStart, setNeedManualStart] = useState(false); // обе озвучки недоступны

  const grid = useMemo(
    () => (Array.isArray(session?.grid) ? session.grid : []),
    [session],
  );
  const softLimitMs = useMemo(() => {
    const s = Number(session?.config?.softLimitSec);
    return s > 0 ? s * 1000 : DEFAULT_SOFT_LIMIT_MS;
  }, [session]);

  const clearTimers = useCallback(() => {
    window.clearInterval(timerRef.current);
    window.clearTimeout(flashTimerRef.current);
    window.clearTimeout(softLimitRef.current);
  }, []);

  // Финиш: авто (клик по «49») либо досрочный («Готово»/лимит — время в finishMs).
  const finishRun = useCallback(async (auto) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearTimers();
    const finishMs = startedAtRef.current && !auto
      ? Date.now() - startedAtRef.current
      : null;
    const clicks = clicksRef.current.slice(0, MAX_CLICKS);

    setPhase('saving');
    try {
      // stepId — ПО ТАБЛИЦЕ ('s01'..'s20'), оба занятия пары пишут в один шаг.
      const res = await saveSchulteReport({
        patientId,
        stepId: stepIdForDay(day),
        clicks,
        finishMs,
        today: localToday(),
      });
      setResult(res);
      setPhase('done');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось сохранить результат.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [patientId, day, clearTimers]);

  const finishRef = useRef(finishRun);
  useEffect(() => {
    finishRef.current = finishRun;
  }, [finishRun]);

  // Нажатие по ячейке: верное — зелёная (навсегда), мимо — красная вспышка.
  // Протокол пишется целиком, счёт потом пересчитывает сервер.
  const onCellClick = useCallback((n, index) => {
    if (finishedRef.current || !startedAtRef.current) return;
    const atMs = Math.max(0, Date.now() - startedAtRef.current);
    if (clicksRef.current.length < MAX_CLICKS) {
      clicksRef.current.push({ n, atMs });
    }
    if (n === expectedRef.current) {
      expectedRef.current += 1;
      setFound((prev) => {
        const next = new Set(prev);
        next.add(n);
        return next;
      });
      if (n === 49) finishRef.current(true); // АВТОФИНИШ по «49»
    } else {
      errorsRef.current += 1;
      setErrorCount(errorsRef.current);
      setFlashing((prev) => {
        const next = new Set(prev);
        next.add(index);
        return next;
      });
      window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = window.setTimeout(() => setFlashing(new Set()), WRONG_FLASH_MS);
    }
  }, []);

  // СТАРТ: секундомер пошёл → снимаем размытие с таблицы (до сих пор она
  // была неактивна, клики игнорировались).
  const startRun = useCallback(() => {
    if (!session || finishedRef.current) return;
    expectedRef.current = 1;
    clicksRef.current = [];
    errorsRef.current = 0;
    startedAtRef.current = Date.now();
    finishedRef.current = false;
    setErrorCount(0);
    setFound(new Set());
    setFlashing(new Set());
    setElapsed(0);
    setPhase('running');

    window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      if (startedAtRef.current && !finishedRef.current) {
        setElapsed(Date.now() - startedAtRef.current);
      }
    }, 200);
    // Мягкий лимит: страховка, если пациент не нашёл всё и не нажал «Готово».
    window.clearTimeout(softLimitRef.current);
    softLimitRef.current = window.setTimeout(() => finishRef.current(false), softLimitMs);
  }, [session, softLimitMs]);

  const startRunRef = useRef(startRun);
  useEffect(() => {
    startRunRef.current = startRun;
  }, [startRun]);

  // ─── Голосовая инструкция — БЕЗ отдельной страницы и кнопки «Начать» ───
  // 1) mp3 из Storage сыграл до конца → размытие снято, стартует секундомер;
  // 2) файл не сыграл (нет/заглушён автоплей/ошибка) → синтез речи;
  // 3) и он недоступен → поверх таблицы кнопка «Начать» (единственный случай).

  const stopAudio = useCallback(() => {
    window.clearTimeout(audioCapRef.current);
    window.clearTimeout(ttsGuardRef.current);
    try {
      audioRef.current?.pause();
    } catch { /* ignore */ }
    audioRef.current = null;
    try {
      window.speechSynthesis?.cancel();
    } catch { /* ignore */ }
  }, []);

  const speakFallback = useCallback(() => {
    let done = false;
    const finish = (spoke) => {
      if (done) return;
      done = true;
      window.clearTimeout(ttsGuardRef.current);
      if (spoke) {
        startRunRef.current();          // синтез закончился → секундомер
      } else {
        setNeedManualStart(true);       // ни файла, ни синтеза — вручную
      }
    };
    try {
      if (!('speechSynthesis' in window)) {
        finish(false);
        return;
      }
      const u = new SpeechSynthesisUtterance(INSTRUCTION_SPEECH);
      u.lang = 'ru-RU';
      u.onend = () => finish(true);
      u.onerror = () => finish(false);
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
      // страховка: TTS молчит (нет русского голоса и т.п.) — 20 сек и вручную
      window.clearTimeout(ttsGuardRef.current);
      ttsGuardRef.current = window.setTimeout(() => finish(false), 20_000);
    } catch {
      finish(false);
    }
  }, []);

  const playInstruction = useCallback(() => {
    let done = false;
    const finish = (played) => {
      if (done) return;
      done = true;
      window.clearTimeout(audioCapRef.current);
      if (played) {
        try {
          audioRef.current?.pause();
        } catch { /* ignore */ }
        audioRef.current = null;
        startRunRef.current();          // озвучка кончилась → СЕКУНДОМЕР
      } else {
        speakFallback();                // файла нет/заглушён → синтез речи
      }
    };
    try {
      stopAudio();
      const a = new Audio(INSTRUCTION_AUDIO_URL);
      a.preload = 'auto';
      audioRef.current = a;
      a.onended = () => finish(true);
      a.onerror = () => finish(false);
      window.clearTimeout(audioCapRef.current);
      audioCapRef.current = window.setTimeout(() => finish(true), AUDIO_CAP_MS);
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => finish(false));
    } catch {
      finish(false);
    }
  }, [stopAudio, speakFallback]);

  // АВТОЗАПУСК: задание загружено → сразу звучит инструкция (поверх таблицы);
  // по её окончании startRun снимет размытие и включит секундомер. Кнопки нет.
  useEffect(() => {
    if (phase !== 'ready' || !session) return undefined;
    setNeedManualStart(false);
    playInstruction();
    return stopAudio;
  }, [phase, session, playInstruction, stopAudio]);

  const load = useCallback(async () => {
    setPhase('loading');
    setErrorMsg('');
    setErrorHint('');
    setSession(null);
    setResult(null);
    clearTimers();
    finishedRef.current = false;
    startedAtRef.current = 0;
    expectedRef.current = 1;
    clicksRef.current = [];
    errorsRef.current = 0;
    setElapsed(0);
    setFound(new Set());
    setFlashing(new Set());
    setErrorCount(0);
    try {
      const params = new URLSearchParams(window.location.search);
      const pidOverride = params.get('patientId');
      const dayOverride = Number(params.get('day')) || null;

      const uid = pidOverride || (await getAuthUserId());
      if (!uid) {
        setErrorMsg('Не удалось определить пользователя. Войдите в аккаунт и откройте тренажёр заново.');
        setPhase('error');
        return;
      }

      const list = await listTrainerReports({ limit: 200 });
      const rows = Array.isArray(list) ? list : list?.items || [];
      const d = dayOverride || resolveDayFromReports(rows, localToday());
      // Занятие d → таблица ceil(d/2) (2 занятия подряд на таблицу).
      const data = await getSchulteSession(tableForDay(d));

      setPatientId(uid);
      setDay(d);
      setSession(data);
      // Сразу экран ЗАДАНИЯ с автозвучкой: таблица видна, но размыта;
      // по окончании озвучки секундомер стартует сам (протокол методички).
      setPhase('ready');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось загрузить задание.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [clearTimers]);

  useEffect(() => {
    void load();
  }, [load]);

  // Предупреждение при случайном закрытии вкладки посреди задания.
  useEffect(() => {
    if (phase !== 'running') return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  // Остановить таймеры и звук при размонтировании страницы.
  useEffect(() => () => { clearTimers(); stopAudio(); }, [clearTimers, stopAudio]);

  const dayNum = Number(day) || 0;
  const tableNum = Number(session?.table ?? tableForDay(dayNum));
  const resElapsed = result?.elapsedMs;
  const resErrors = Number(result?.errors ?? 0);
  const resCount = Number(result?.count ?? 0);
  const resTotal = Number(result?.total ?? 49);
  const resCompleted = Boolean(result?.completed);

  return (
    <div className="flex min-h-screen flex-col bg-cvx-background px-4 py-6">
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col">
        {/* Шапка: логотип 1:1 с лендингом + «Назад» */}
        <header className="flex items-center justify-between gap-3">
          <a href="/" aria-label="Cortivox.ai на главную" className="flex items-center gap-2.5">
            <span className="flex size-8 items-center justify-center rounded-[10px] bg-cvx-ink text-white">
              <BrainCircuit className="size-4" />
            </span>
            <span className="text-[16px] font-semibold tracking-[-0.04em] text-cvx-ink">
              Cortivox<span className="text-cvx-logo-ai">.ai</span>
            </span>
          </a>
          <button type="button" onClick={() => window.history.back()} className="cvx-back">
            <ArrowLeft className="size-4" /> Назад
          </button>
        </header>

        {/* ЗАГРУЗКА */}
        {phase === 'loading' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-cvx-muted-foreground">
            <Loader2 className="size-8 animate-spin text-cvx-primary" />
            <p className="text-sm font-semibold">Загружаем задание…</p>
          </div>
        )}

        {/* СОХРАНЕНИЕ */}
        {phase === 'saving' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-cvx-muted-foreground">
            <Loader2 className="size-8 animate-spin text-cvx-primary" />
            <p className="text-sm font-semibold">Сохраняем результат…</p>
          </div>
        )}

        {/* ЗАДАНИЕ: экран задания — он же экран инструкции (отдельной страницы нет).
            В 'ready' таблица размыта и неактивна, секундомер 0:00; старт — автоматически
            по окончании озвучки. */}
        {(phase === 'running' || phase === 'ready') && session && (
          <section className="flex flex-1 flex-col items-center pt-6">
            <span className="inline-flex items-center rounded-full bg-cvx-accent px-4 py-1.5 text-sm font-bold text-cvx-accent-foreground">
              День {dayNum} · Таблица {tableNum} · {session.daysLabel}
            </span>

            {/* СЕКУНДОМЕР: 0:00, пока звучит инструкция */}
            <div className={`mt-4 flex items-center gap-3 rounded-2xl border border-cvx-border bg-cvx-card px-6 py-3 shadow-sm transition-opacity ${
              phase === 'ready' ? 'opacity-60' : ''
            }`}>
              <Timer className="size-5 text-cvx-primary" aria-hidden="true" />
              <span className="text-4xl font-bold tabular-nums tracking-tight text-cvx-foreground" aria-live="off">
                {fmtElapsed(elapsed)}
              </span>
            </div>

            {/* ТАБЛИЦА 7×7: верное — зелёное и остаётся, мимо — красная вспышка.
                Пока звучит инструкция — размыта и не кликается (изучить заранее
                нельзя); озвучка кончилась → размытие снято, время пошло. */}
            <div className="relative mt-5 w-full max-w-md">
              <div
                aria-label={`Таблица Шульте ${tableNum}: числа от 1 до 49`}
                className={`grid w-full grid-cols-7 gap-1.5 transition-all duration-700 ${
                  phase === 'running' ? '' : 'pointer-events-none select-none opacity-60 blur-md'
                }`}
              >
                {grid.map((n, index) => {
                  const isFound = found.has(n);
                  const isWrong = flashing.has(index);
                  return (
                    <button
                      key={`${index}-${n}`}
                      type="button"
                      aria-label={`Число ${n}`}
                      disabled={isFound || phase !== 'running'}
                      onClick={() => onCellClick(n, index)}
                      className={`flex aspect-square items-center justify-center rounded-xl border text-lg font-bold tabular-nums transition-colors duration-150 sm:text-xl ${
                        isFound
                          ? 'border-[oklch(0.7_0.15_150)] bg-[oklch(0.93_0.06_150)] text-[oklch(0.4_0.1_150)]'
                          : isWrong
                            ? 'border-cvx-destructive bg-cvx-destructive/10 text-cvx-destructive'
                            : 'border-cvx-border bg-cvx-card text-cvx-foreground hover:bg-cvx-secondary'
                      }`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>

              {/* Поверх таблицы, пока идёт озвучка: плашка «Слушайте инструкцию».
                  Кнопка «Начать» — ТОЛЬКО если не сработали ни mp3, ни синтез речи. */}
              {phase === 'ready' && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  {needManualStart ? (
                    <button
                      type="button"
                      onClick={() => startRunRef.current()}
                      className="cv-large inline-flex items-center gap-2.5 rounded-full bg-cvx-ink px-10 py-4 text-lg font-bold text-white shadow-lg transition-colors hover:bg-cvx-ink/90"
                    >
                      <Play className="size-5" /> Начать
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-2 rounded-full bg-cvx-ink/90 px-5 py-2.5 text-sm font-bold text-white shadow-lg">
                      <Loader2 className="size-4 animate-spin" />
                      Слушайте инструкцию
                    </span>
                  )}
                </div>
              )}
            </div>

            {phase === 'running' && (
              <>
            {/* Прогресс + ошибки (числа-подсказки НЕ показываем — только счётчики) */}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2" aria-live="polite">
              <span className="rounded-full bg-cvx-secondary px-3 py-1 text-xs font-bold text-cvx-secondary-foreground">
                Найдено {found.size} из 49
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                errorCount > 0 ? 'bg-cvx-warn text-cvx-warn' : 'bg-cvx-secondary text-cvx-secondary-foreground'
              }`}>
                {errorsLabel(errorCount)}
              </span>
            </div>

            <p className="mt-4 text-center text-sm font-semibold text-cvx-muted-foreground">
              Нажимайте числа по порядку: следующее — {Math.min(found.size + 1, 49)}
            </p>

            <button
              type="button"
              onClick={() => finishRef.current(false)}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-cvx-border bg-cvx-card px-6 py-3 text-sm font-bold text-cvx-ink transition-colors hover:bg-cvx-secondary"
            >
              Готово
            </button>
              </>
            )}
          </section>
        )}

        {/* РЕЗУЛЬТАТ */}
        {phase === 'done' && (
          <section className="mt-6 flex flex-1 flex-col">
            <div className="rounded-3xl border border-cvx-border bg-cvx-card p-6 text-center shadow-sm sm:p-8">
              <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-cvx-accent">
                <CheckCircle2 className="size-7 text-cvx-accent-foreground" />
              </span>
              <h1 className="mt-4 text-xl font-bold text-cvx-foreground">Задание выполнено — результат сохранён</h1>
              <p className="mt-1 text-sm font-semibold text-cvx-muted-foreground">
                День {dayNum} · Таблица {tableNum} · {session?.daysLabel}
              </p>

              <p className="mt-6 text-6xl font-bold tabular-nums text-cvx-primary">
                {resElapsed != null ? fmtElapsed(resElapsed) : '—'}
              </p>
              <p className="mt-1 text-sm font-semibold text-cvx-muted-foreground">время выполнения</p>

              <div className="mt-5 flex flex-col gap-2.5 text-left">
                <div className="rounded-2xl bg-cvx-muted p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cvx-muted-foreground/60">
                    Найдено чисел
                  </p>
                  <p className="mt-1.5 text-sm font-bold text-cvx-foreground">
                    {resCount} из {resTotal}
                    {resCompleted ? ' — вся таблица' : ' — задание закрыто досрочно'}
                  </p>
                </div>
                <div className={`rounded-2xl p-4 ${resErrors > 0 ? 'border border-cvx-warn/30 bg-cvx-warn-card' : 'bg-cvx-muted'}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cvx-muted-foreground/60">
                    Нажатия мимо порядка
                  </p>
                  <p className="mt-1.5 text-sm font-bold text-cvx-foreground">
                    {errorsLabel(resErrors)}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:justify-center">
                <button
                  type="button"
                  onClick={() => void load()}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cvx-primary px-6 py-3 text-sm font-bold text-cvx-primary-foreground transition-colors hover:bg-cvx-primary/90"
                >
                  <RotateCcw className="size-4" /> Пройти ещё раз
                </button>
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="inline-flex items-center justify-center rounded-full border border-cvx-border bg-cvx-card px-6 py-3 text-sm font-bold text-cvx-ink transition-colors hover:bg-cvx-secondary"
                >
                  Готово
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ОШИБКА */}
        {phase === 'error' && (
          <section className="mt-6 flex flex-1 flex-col">
            <div className="rounded-3xl border border-cvx-border bg-cvx-card p-6 text-center shadow-sm sm:p-8">
              <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-cvx-warn">
                <AlertTriangle className="size-7 text-cvx-warn" />
              </span>
              <h1 className="mt-4 text-xl font-bold text-cvx-foreground">Что-то пошло не так</h1>
              <p className="mt-2 text-sm leading-relaxed text-cvx-muted-foreground">{errorMsg}</p>
              {errorHint && <p className="mt-2 text-xs leading-relaxed text-cvx-muted-foreground">{errorHint}</p>}
              <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:justify-center">
                <button
                  type="button"
                  onClick={() => void load()}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cvx-primary px-6 py-3 text-sm font-bold text-cvx-primary-foreground transition-colors hover:bg-cvx-primary/90"
                >
                  <RotateCcw className="size-4" /> Повторить
                </button>
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="inline-flex items-center justify-center rounded-full border border-cvx-border bg-cvx-card px-6 py-3 text-sm font-bold text-cvx-ink transition-colors hover:bg-cvx-secondary"
                >
                  Назад
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
